import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type { AllRawData } from '../../raw-types.ts';

// Server-side league table — the in-game wave/enemy/shop data referenced by
// `serverLeagueSeasons.leagueTableId`. Multiple seasons share a single table;
// shipping the waves once here is a large win over inlining per season.
export const LeagueTableSchema = defineSchema({
  name: 'serverLeagueTables',
  fields: [
    ['id', 'number'],
    ['waves', { kind: 'array', of: 'stageWave' }],
    // 콘텐츠 row 에 지정된 FIXED 난이도 템플릿 ID. 서버가 submit 시 재도출에 사용.
    ['fixedDifficultyTemplateId', 'string?'],
  ],
  lookupKeys: ['id'],
});

export interface RawLeagueTableRow {
  id: number;
  fixed_difficulty_template_id?: string | null;
}

export function leagueTableFromRaw(
  row: RawLeagueTableRow,
  raw: Pick<AllRawData, 'league_table_wave' | 'league_table_wave_enemy'>
) {
  const tableWaves = (raw.league_table_wave ?? [])
    .filter((w) => w.league_table_id === row.id)
    .sort((a, b) => a.wave_number - b.wave_number);
  const lastWaveNumber = tableWaves.length > 0 ? tableWaves[tableWaves.length - 1].wave_number : -1;

  const waves = tableWaves.map((w) => {
    const isLastWave = w.wave_number === lastWaveNumber;
    const shopConfigId = !isLastWave && w.shop_config_id != null ? w.shop_config_id : null;

    const enemies = (raw.league_table_wave_enemy ?? [])
      .filter((e) => e.league_table_id === row.id && e.wave_number === w.wave_number)
      .map((e) => ({
        id: e.enemy_id,
        count: 1,
        // Range columns (min/max). Worker uses the upper bound for spawn
        // bottleneck math so the player is given the benefit of the doubt.
        spawnInterval: e.min_spawn_interval ?? e.max_spawn_interval,
        spawnPattern: e.spawn_pattern ?? null,
        healthMultiplier: e.max_health_multiplier,
        speedMultiplier: e.max_speed_multiplier,
        atkMultiplier: e.max_atk_multiplier,
      }));

    return {
      waveNumber: w.wave_number,
      killGoal: w.kill_goal,
      timeLimitSeconds: w.time_limit_seconds,
      silverReward: w.silver_reward,
      shopConfigId,
      enemies,
    };
  });

  return {
    id: row.id,
    waves,
    ...(row.fixed_difficulty_template_id != null
      ? { fixedDifficultyTemplateId: row.fixed_difficulty_template_id }
      : {}),
  };
}

registerCategorySchema('serverLeagueTables', LeagueTableSchema);
